import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  makeStyles,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
} from "@material-ui/core";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";

import {
  Help as HelpIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  VideoLibrary as VideoIcon,
  Title as TitleIcon,
  Description as DescriptionIcon,
  PlayCircleOutline as PlayIcon,
  YouTube as YouTubeIcon,
  Movie as MovieIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from "@material-ui/icons";

import useHelps from "../../hooks/useHelps";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../ConfirmationModal";

/**
 * Valida si una URL es válida y accesible
 * @param {string} url - URL a validar
 * @returns {boolean} - true si la URL es válida
 */
const isValidUrl = (url) => {
  try {
    const urlObj = new URL(url);
    // Solo permitir http y https
    return urlObj.protocol === "http:" || urlObj.protocol === "https:";
  } catch (e) {
    return false;
  }
};

/**
 * Detecta el tipo de video (YouTube o externo) y retorna el tipo y valor procesado
 * @param {string} input - URL o código de video
 * @returns {{type: 'youtube'|'external', value: string, error?: string}|null}
 */
const detectVideoType = (input) => {
  if (!input || typeof input !== "string") return null;

  const trimmedInput = input.trim();
  if (!trimmedInput) return null;

  // Patrones para detectar YouTube
  const youtubePatterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
  ];

  // Verificar si es URL de YouTube
  for (const pattern of youtubePatterns) {
    const match = trimmedInput.match(pattern);
    if (match && match[1]) {
      return {
        type: "youtube",
        value: match[1], // Código de 11 caracteres
      };
    }
  }

  // Si es exactamente 11 caracteres alfanuméricos, asumir código de YouTube
  if (trimmedInput.length === 11 && /^[a-zA-Z0-9_-]{11}$/.test(trimmedInput)) {
    return {
      type: "youtube",
      value: trimmedInput,
    };
  }

  // Verificar si es una URL externa (http/https)
  const urlPattern = /^https?:\/\/.+/i;
  if (urlPattern.test(trimmedInput)) {
    // Validar formato de URL
    if (!isValidUrl(trimmedInput)) {
      return {
        type: "external",
        value: null,
        error: i18n.t("helps.validation.urlInvalid"),
      };
    }

    // Verificar si tiene extensión de video (recomendado pero no obligatorio)
    const videoExtensions = /\.(mp4|webm|ogg|ogv|mov|avi|wmv|flv|mkv)(\?.*)?$/i;
    const hasVideoExtension = videoExtensions.test(trimmedInput);

    // Es URL externa
    return {
      type: "external",
      value: encodeURI(trimmedInput), // Codificar URL para manejar espacios y caracteres especiales
      warning: !hasVideoExtension ? i18n.t("helps.validation.urlExtensionHint") : null,
    };
  }

  // Si no coincide con ningún patrón, retornar null
  return null;
};

/**
 * Procesa el input del usuario y retorna el valor procesado según el tipo de video
 * @param {string} input - URL o código de video ingresado por el usuario
 * @returns {string|null} - Valor procesado para guardar en BD, o null si no es válido
 */
const processVideoInput = (input) => {
  const detected = detectVideoType(input);

  if (!detected) {
    return null;
  }

  return detected.value;
};

const getVideoPreviewThumbnail = (record) => {
  if (record?.thumbnail?.trim()) {
    return record.thumbnail.trim();
  }

  const detected = detectVideoType(record?.video);

  if (detected?.type === "youtube") {
    return `https://img.youtube.com/vi/${detected.value}/mqdefault.jpg`;
  }

  return null;
};

const getVideoType = (video) => detectVideoType(video)?.type || "unknown";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: theme.spacing(2),
  },
  // ✅ CORREGIDO: FormCard dinámico según tema
  formCard: {
    borderRadius: theme.spacing(1),
    boxShadow: theme.mode === "dark" ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.1)",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === "dark" ? "#4B5563" : "#e0e0e0"}`,
    marginBottom: theme.spacing(3),
  },
  formCardContent: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
  },
  formTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.primary.main,
    textAlign: "center",
    justifyContent: "center",
  },
  formGrid: {
    marginTop: theme.spacing(1),
  },
  fieldContainer: {
    marginBottom: theme.spacing(1),
  },
  // ✅ CORREGIDO: TextField dinámico según tema
  textField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.mode === "dark" ? "#374151" : theme.palette.background.paper,
      borderRadius: theme.spacing(1),
      color: theme.palette.text.primary,
      "& fieldset": {
        borderColor: theme.mode === "dark" ? "#4B5563" : "#e0e0e0",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&:hover": {
        backgroundColor: theme.mode === "dark" ? "#4B5563" : "#fafafa",
      },
      "&.Mui-focused": {
        backgroundColor: theme.mode === "dark" ? "#374151" : theme.palette.background.paper,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
      fontWeight: "500",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
  buttonContainer: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "center",
    marginTop: theme.spacing(2),
    flexWrap: "wrap",
  },
  actionButton: {
    borderRadius: theme.spacing(1),
    textTransform: "none",
    fontWeight: "600",
    padding: theme.spacing(1, 2),
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
    "&:hover": {
      boxShadow: "0 2px 6px rgba(0,0,0,0.16)",
    },
    transition: "all 0.2s ease",
    minWidth: 100,
  },
  primaryButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  secondaryButton: {
    backgroundColor: theme.palette.grey[500],
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.grey[600],
    },
  },
  errorButton: {
    backgroundColor: theme.palette.error.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
  visibilityButton: {
    color: theme.palette.success.main,
    "&:hover": {
      backgroundColor:
        theme.mode === "dark" ? "rgba(76, 175, 80, 0.18)" : "rgba(76, 175, 80, 0.12)",
    },
  },
  hiddenButton: {
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.mode === "dark" ? "rgba(156, 163, 175, 0.18)" : "rgba(0, 0, 0, 0.08)",
    },
  },
  hiddenRow: {
    backgroundColor: theme.mode === "dark" ? "rgba(55, 65, 81, 0.35)" : "#fafafa",
  },
  sectionTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  // ✅ CORREGIDO: TableCard dinámico según tema
  tableCard: {
    borderRadius: theme.spacing(1),
    boxShadow: theme.mode === "dark" ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.1)",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === "dark" ? "#4B5563" : "#e0e0e0"}`,
    overflow: "hidden",
  },
  libraryHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.mode === "dark" ? "#4B5563" : "#E5E7EB"}`,
    flexWrap: "wrap",
  },
  libraryTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  summaryGrid: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  summaryItem: {
    minWidth: 96,
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.mode === "dark" ? "#4B5563" : "#E5E7EB"}`,
    backgroundColor: theme.mode === "dark" ? "#111827" : "#F9FAFB",
  },
  summaryNumber: {
    fontSize: "1rem",
    fontWeight: 700,
    lineHeight: 1.1,
    color: theme.palette.text.primary,
  },
  summaryLabel: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  tableScroller: {
    overflowX: "auto",
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "& .MuiTableCell-head": {
      color: "white",
      fontWeight: "bold",
    },
  },
  // ✅ CORREGIDO: TableRow dinámico según tema
  tableRow: {
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      backgroundColor: theme.mode === "dark" ? "#374151" : theme.palette.action.hover,
    },
    cursor: "pointer",
  },
  // ✅ CORREGIDO: TableCell dinámico según tema
  tableCell: {
    padding: theme.spacing(1),
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.mode === "dark" ? "#4B5563" : "rgba(224, 224, 224, 0.5)"}`,
  },
  editButton: {
    color: theme.palette.primary.main,
    "&:hover": {
      backgroundColor:
        theme.mode === "dark" ? "rgba(59, 130, 246, 0.2)" : theme.palette.primary.light,
      color: theme.mode === "dark" ? theme.palette.primary.light : "white",
    },
  },
  videoChip: {
    backgroundColor: theme.mode === "dark" ? "#374151" : "#EEF2FF",
    color: theme.mode === "dark" ? "#E5E7EB" : "#3730A3",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    maxWidth: 240,
  },
  previewCell: {
    width: 136,
  },
  thumbnail: {
    width: 112,
    height: 64,
    borderRadius: theme.spacing(0.75),
    overflow: "hidden",
    backgroundColor: theme.mode === "dark" ? "#111827" : "#111827",
    border: `1px solid ${theme.mode === "dark" ? "#4B5563" : "#D1D5DB"}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    position: "relative",
  },
  thumbnailImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  thumbnailIcon: {
    position: "absolute",
    right: 6,
    bottom: 6,
    backgroundColor: "rgba(0, 0, 0, 0.72)",
    color: "#fff",
    borderRadius: "50%",
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  contentCell: {
    minWidth: 280,
  },
  titleLine: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  descriptionLine: {
    color: theme.palette.text.secondary,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    lineHeight: 1.35,
  },
  metaLine: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    flexWrap: "wrap",
  },
  sourceCell: {
    minWidth: 220,
    maxWidth: 280,
  },
  sourceCode: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    wordBreak: "break-all",
    marginTop: theme.spacing(0.75),
  },
  visibilityCell: {
    minWidth: 132,
  },
  actionsCell: {
    minWidth: 176,
    whiteSpace: "nowrap",
  },
  orderButton: {
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.mode === "dark" ? "rgba(156, 163, 175, 0.18)" : "#F3F4F6",
      color: theme.palette.primary.main,
    },
  },
  visibleChip: {
    backgroundColor: theme.palette.success.main,
    color: "white",
    fontWeight: 600,
  },
  hiddenChip: {
    backgroundColor: theme.palette.grey[500],
    color: "white",
    fontWeight: 600,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  helperText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
}));

export function HelpFormImproved(props) {
  const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
  const classes = useStyles();

  const [record, setRecord] = useState({
    title: "",
    description: "",
    video: "",
    isVisible: true,
  });

  const [detectedVideoType, setDetectedVideoType] = useState(null); // 'youtube', 'external', o null

  useEffect(() => {
    setRecord(initialValue);
    // Detectar tipo del video inicial si existe
    if (initialValue?.video) {
      const detected = detectVideoType(initialValue.video);
      setDetectedVideoType(detected ? detected.type : null);
    } else {
      setDetectedVideoType(null);
    }
  }, [initialValue]);

  const handleSubmit = async (data) => {
    // Validar que haya un video ingresado
    if (!data.video || !data.video.trim()) {
      toast.error("Por favor ingresa una URL de YouTube o una URL externa de video");
      return;
    }

    // Si es una actualización y el video no cambió, usar el valor original sin procesar
    // Esto evita doble codificación de URLs ya codificadas
    const videoChanged = !record.id || data.video !== record.video;

    if (!videoChanged) {
      // El video no cambió, usar el valor original (ya codificado de BD)
      // No procesar para evitar doble codificación
      onSubmit(data);
      return;
    }

    // El video cambió, procesar normalmente
    // Primero decodificar si ya está codificado para evitar doble codificación
    let videoToProcess = data.video.trim();
    try {
      // Intentar decodificar si parece estar codificado
      if (videoToProcess.includes("%")) {
        const decoded = decodeURIComponent(videoToProcess);
        // Si la decodificación fue exitosa y es diferente, usar la decodificada
        if (decoded !== videoToProcess && isValidUrl(decoded)) {
          videoToProcess = decoded;
        }
      }
    } catch (e) {
      // Si falla la decodificación, usar el valor original
      // (puede ser que no esté codificado o tenga formato especial)
    }

    // Detectar tipo y validar
    const detected = detectVideoType(videoToProcess);

    if (!detected) {
      toast.error(i18n.t("helps.validation.invalidVideoInput"));
      return;
    }

    // Verificar si hay error en la detección
    if (detected.error) {
      toast.error(detected.error);
      return;
    }

    // Procesar el input del video
    const processedVideo = processVideoInput(videoToProcess);

    if (!processedVideo) {
      toast.error(i18n.t("helps.validation.processFailed"));
      return;
    }

    // Mostrar advertencia si existe
    if (detected.warning) {
      toast.warning(detected.warning, { autoClose: 5000 });
    }

    // Asignar el video procesado
    data.video = processedVideo;

    // Mostrar mensaje de éxito según tipo
    if (detected.type === "youtube") {
      toast.success(i18n.t("helps.toast.youtubeDetected", { code: processedVideo }));
    } else if (detected.type === "external") {
      toast.success(i18n.t("helps.toast.externalDetected"));
    }

    onSubmit(data);
  };

  return (
    <Card className={classes.formCard}>
      <CardContent className={classes.formCardContent}>
        <Typography variant="h5" className={classes.formTitle}>
          <HelpIcon />
          {record.id ? i18n.t("helps.formTitle.edit") : i18n.t("helps.formTitle.create")}
        </Typography>

        <Formik
          enableReinitialize
          initialValues={record}
          onSubmit={(values, { resetForm }) =>
            setTimeout(() => {
              handleSubmit(values);
              resetForm();
            }, 500)
          }
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Grid container spacing={2} className={classes.formGrid}>
                {/* Información del Video */}
                <Grid item xs={12}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    <VideoIcon />
                    {i18n.t("helps.sectionVideo")}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <div className={classes.fieldContainer}>
                    <TextField
                      label={i18n.t("helps.form.title")}
                      name="title"
                      value={values.title || ""}
                      onChange={(e) => setFieldValue("title", e.target.value)}
                      variant="outlined"
                      fullWidth
                      className={classes.textField}
                      InputProps={{
                        startAdornment: <TitleIcon style={{ marginRight: 8, color: "#666" }} />,
                      }}
                    />
                  </div>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <div className={classes.fieldContainer}>
                    <TextField
                      label={i18n.t("helps.form.videoCode")}
                      name="video"
                      value={values.video || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFieldValue("video", value);
                        // Detectar tipo en tiempo real
                        const detected = detectVideoType(value);
                        setDetectedVideoType(detected ? detected.type : null);
                      }}
                      variant="outlined"
                      fullWidth
                      className={classes.textField}
                      placeholder={i18n.t("helps.placeholders.video")}
                      InputProps={{
                        startAdornment:
                          detectedVideoType === "external" ? (
                            <MovieIcon style={{ marginRight: 8, color: "#666" }} />
                          ) : (
                            <YouTubeIcon style={{ marginRight: 8, color: "#666" }} />
                          ),
                      }}
                    />
                    <Typography className={classes.helperText}>
                      {detectedVideoType === "youtube"
                        ? i18n.t("helps.helpers.youtubeDetected")
                        : detectedVideoType === "external"
                          ? i18n.t("helps.helpers.externalDetected")
                          : i18n.t("helps.helpers.pasteHint")}
                    </Typography>
                  </div>
                </Grid>

                <Grid item xs={12}>
                  <div className={classes.fieldContainer}>
                    <TextField
                      label={i18n.t("helps.form.description")}
                      name="description"
                      value={values.description || ""}
                      onChange={(e) => setFieldValue("description", e.target.value)}
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={3}
                      className={classes.textField}
                      InputProps={{
                        startAdornment: (
                          <DescriptionIcon
                            style={{
                              marginRight: 8,
                              color: "#666",
                              alignSelf: "flex-start",
                              marginTop: 8,
                            }}
                          />
                        ),
                      }}
                    />
                  </div>
                </Grid>

                <Grid item xs={12}>
                  <div className={classes.fieldContainer}>
                    <TextField
                      label={i18n.t("helps.thumbnail.label")}
                      name="thumbnail"
                      value={values.thumbnail || ""}
                      onChange={(e) => setFieldValue("thumbnail", e.target.value)}
                      variant="outlined"
                      fullWidth
                      className={classes.textField}
                      placeholder={i18n.t("helps.placeholders.thumbnail")}
                      InputProps={{
                        startAdornment: <VideoIcon style={{ marginRight: 8, color: "#666" }} />,
                      }}
                    />
                    <Typography className={classes.helperText}>
                      {i18n.t("helps.thumbnail.hint")}
                    </Typography>
                  </div>
                </Grid>
              </Grid>

              {/* Botones de Acción */}
              <div className={classes.buttonContainer}>
                <Button
                  variant="contained"
                  className={`${classes.actionButton} ${classes.secondaryButton}`}
                  onClick={onCancel}
                  startIcon={<ClearIcon />}
                  disabled={loading}
                >
                  {i18n.t("helps.buttons.clear")}
                </Button>

                {record.id !== undefined && (
                  <Button
                    variant="contained"
                    className={`${classes.actionButton} ${classes.errorButton}`}
                    onClick={() => onDelete(record)}
                    startIcon={<DeleteIcon />}
                    disabled={loading}
                  >
                    {i18n.t("helps.buttons.delete")}
                  </Button>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  className={`${classes.actionButton} ${classes.primaryButton}`}
                  disabled={loading}
                  startIcon={
                    loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />
                  }
                >
                  {loading ? i18n.t("helps.buttons.saving") : i18n.t("helps.buttons.save")}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </CardContent>
    </Card>
  );
}

export function HelpsManagerGridImproved(props) {
  const { records, onSelect, onToggleVisibility, onMove, loading } = props;
  const classes = useStyles();
  const visibleCount = records.filter((record) => record.isVisible !== false).length;
  const hiddenCount = records.length - visibleCount;
  const youtubeCount = records.filter((record) => getVideoType(record.video) === "youtube").length;
  const externalCount = records.filter(
    (record) => getVideoType(record.video) === "external"
  ).length;

  const renderVideoCode = (videoCode) => {
    if (!videoCode) return "-";

    return (
      <Chip label={videoCode} size="small" className={classes.videoChip} icon={<PlayIcon />} />
    );
  };

  if (records.length === 0) {
    return (
      <Card className={classes.tableCard}>
        <CardContent className={classes.emptyState}>
          <Typography variant="h6" color="textSecondary">
            {i18n.t("helps.grid.emptyTitle")}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {i18n.t("helps.grid.emptySubtitle")}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.tableCard}>
      <Box className={classes.libraryHeader}>
        <Typography variant="h6" className={classes.libraryTitle}>
          <VideoIcon />
          {i18n.t("helps.library.title")}
        </Typography>
        <Box className={classes.summaryGrid}>
          <Box className={classes.summaryItem}>
            <Typography className={classes.summaryNumber}>{records.length}</Typography>
            <Typography className={classes.summaryLabel}>
              {i18n.t("helps.summary.total")}
            </Typography>
          </Box>
          <Box className={classes.summaryItem}>
            <Typography className={classes.summaryNumber}>{visibleCount}</Typography>
            <Typography className={classes.summaryLabel}>
              {i18n.t("helps.summary.visible")}
            </Typography>
          </Box>
          <Box className={classes.summaryItem}>
            <Typography className={classes.summaryNumber}>{hiddenCount}</Typography>
            <Typography className={classes.summaryLabel}>
              {i18n.t("helps.summary.hidden")}
            </Typography>
          </Box>
          <Box className={classes.summaryItem}>
            <Typography className={classes.summaryNumber}>
              {youtubeCount}/{externalCount}
            </Typography>
            <Typography className={classes.summaryLabel}>
              {i18n.t("helps.summary.types")}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box className={classes.tableScroller}>
        <Table>
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell align="left" className={classes.previewCell}>
                {i18n.t("helps.table.preview")}
              </TableCell>
              <TableCell align="left" className={classes.contentCell}>
                <TitleIcon style={{ marginRight: 8 }} />
                {i18n.t("helps.table.content")}
              </TableCell>
              <TableCell align="left" className={classes.sourceCell}>
                <VideoIcon style={{ marginRight: 8 }} />
                {i18n.t("helps.table.source")}
              </TableCell>
              <TableCell align="center" className={classes.visibilityCell}>
                <VisibilityIcon style={{ marginRight: 8 }} />
                {i18n.t("helps.table.visibility")}
              </TableCell>
              <TableCell align="center" className={classes.actionsCell}>
                {i18n.t("helps.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((row, index) => {
              const preview = getVideoPreviewThumbnail(row);
              const videoType = getVideoType(row.video);

              return (
                <TableRow
                  className={`${classes.tableRow} ${row.isVisible === false ? classes.hiddenRow : ""}`}
                  key={row.id || row.video}
                  onClick={() => onSelect(row)}
                >
                  <TableCell align="left" className={`${classes.tableCell} ${classes.previewCell}`}>
                    <Box className={classes.thumbnail}>
                      {preview ? (
                        <img
                          src={preview}
                          alt={row.title || "Video"}
                          className={classes.thumbnailImage}
                        />
                      ) : (
                        <MovieIcon />
                      )}
                      <Box className={classes.thumbnailIcon}>
                        <PlayIcon fontSize="small" />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="left" className={`${classes.tableCell} ${classes.contentCell}`}>
                    <Typography variant="body2" className={classes.titleLine}>
                      {row.title || "-"}
                    </Typography>
                    <Typography variant="body2" className={classes.descriptionLine}>
                      {row.description || i18n.t("helps.library.noDescription")}
                    </Typography>
                    <Box className={classes.metaLine}>
                      <Chip
                        size="small"
                        icon={videoType === "youtube" ? <YouTubeIcon /> : <MovieIcon />}
                        label={i18n.t(`helps.videoTypes.${videoType}`)}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="left" className={`${classes.tableCell} ${classes.sourceCell}`}>
                    {renderVideoCode(row.video)}
                    {row.thumbnail ? (
                      <Typography className={classes.sourceCode}>
                        {i18n.t("helps.thumbnail.label")}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell
                    align="center"
                    className={`${classes.tableCell} ${classes.visibilityCell}`}
                  >
                    <Chip
                      size="small"
                      icon={row.isVisible === false ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      label={
                        row.isVisible === false
                          ? i18n.t("helps.visibility.hidden")
                          : i18n.t("helps.visibility.visible")
                      }
                      className={row.isVisible === false ? classes.hiddenChip : classes.visibleChip}
                    />
                  </TableCell>
                  <TableCell
                    align="center"
                    className={`${classes.tableCell} ${classes.actionsCell}`}
                  >
                    <Tooltip title={i18n.t("helps.tooltips.editRow")}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelect(row);
                        }}
                        className={classes.editButton}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={i18n.t("helps.tooltips.moveUp")}>
                      <span>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            onMove(index, -1);
                          }}
                          className={classes.orderButton}
                          size="small"
                          disabled={loading || index === 0}
                        >
                          <ArrowUpwardIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={i18n.t("helps.tooltips.moveDown")}>
                      <span>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            onMove(index, 1);
                          }}
                          className={classes.orderButton}
                          size="small"
                          disabled={loading || index === records.length - 1}
                        >
                          <ArrowDownwardIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        row.isVisible === false
                          ? i18n.t("helps.tooltips.show")
                          : i18n.t("helps.tooltips.hide")
                      }
                    >
                      <span>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleVisibility(row);
                          }}
                          className={
                            row.isVisible === false
                              ? classes.hiddenButton
                              : classes.visibilityButton
                          }
                          size="small"
                          disabled={loading}
                        >
                          {row.isVisible === false ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Card>
  );
}

export default function HelpsManagerImproved() {
  const classes = useStyles();
  const { list, save, update, toggleVisibility, updateOrder, remove } = useHelps();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState({
    title: "",
    description: "",
    video: "",
    isVisible: true,
  });

  useEffect(() => {
    loadHelps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHelps = async () => {
    setLoading(true);
    try {
      const helpList = await list();
      setRecords(helpList);
    } catch (e) {
      toast.error(i18n.t("helps.messages.loadError"));
    }
    setLoading(false);
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(data);
      } else {
        await save(data);
      }
      await loadHelps();
      handleCancel();
      toast.success(i18n.t("helps.messages.saveSuccess"));
    } catch (e) {
      toast.error(i18n.t("helps.messages.saveError"));
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      await loadHelps();
      handleCancel();
      toast.success(i18n.t("helps.messages.deleteSuccess"));
    } catch (e) {
      toast.error(i18n.t("helps.messages.deleteError"));
    }
    setLoading(false);
  };

  const handleOpenDeleteDialog = () => {
    setShowConfirmDialog(true);
  };

  const handleCancel = () => {
    setRecord({
      title: "",
      description: "",
      video: "",
      thumbnail: "",
      isVisible: true,
      displayOrder: 0,
    });
  };

  const handleSelect = (data) => {
    setRecord({
      id: data.id,
      title: data.title || "",
      description: data.description || "",
      video: data.video || "",
      thumbnail: data.thumbnail || "",
      isVisible: data.isVisible !== false,
    });
  };

  const handleMove = async (index, direction) => {
    const targetIndex = index + direction;

    if (targetIndex < 0 || targetIndex >= records.length) {
      return;
    }

    const reordered = [...records];
    const [movedRecord] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, movedRecord);

    setRecords(reordered);
    setLoading(true);

    try {
      const orderedIds = reordered.map((item) => item.id);
      const updatedRecords = await updateOrder(orderedIds);
      setRecords(updatedRecords);
      toast.success(i18n.t("helps.messages.orderSuccess"));
    } catch (e) {
      await loadHelps();
      toast.error(i18n.t("helps.messages.orderError"));
    }

    setLoading(false);
  };

  const handleToggleVisibility = async (data) => {
    setLoading(true);
    try {
      const nextVisibility = data.isVisible === false;
      await toggleVisibility(data.id, nextVisibility);
      await loadHelps();
      toast.success(
        nextVisibility ? i18n.t("helps.messages.showSuccess") : i18n.t("helps.messages.hideSuccess")
      );
    } catch (e) {
      toast.error(i18n.t("helps.messages.visibilityError"));
    }
    setLoading(false);
  };

  return (
    <div className={classes.root}>
      <HelpFormImproved
        initialValue={record}
        onDelete={handleOpenDeleteDialog}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        loading={loading}
      />

      <HelpsManagerGridImproved
        records={records}
        onSelect={handleSelect}
        onToggleVisibility={handleToggleVisibility}
        onMove={handleMove}
        loading={loading}
      />

      <ConfirmationModal
        title={i18n.t("helps.messages.deleteTitle")}
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => handleDelete()}
      >
        {i18n.t("helps.messages.deleteMessage")}
      </ConfirmationModal>
    </div>
  );
}
